// jobUtils.js

// Helper function to determine if a user is a dispatch user
export const isDispatchUser = (user) => {
  return user?.primaryRole === 'OW' || user?.primaryRole === 'sOW' || user?.primaryRole === 'RM';
};

// Helper function to determine if a user can reactivate completed jobs
export const canReactivateJob = (user) => {
  if (!user) return false;
  
  // Check primary role (OW, sOW, RM)
  const hasPrimaryRole = user.primaryRole === 'OW' || user.primaryRole === 'sOW' || user.primaryRole === 'RM';
  
  // Check secondary roles (dispatcher, answeringService)
  const hasSecondaryRole = user.secondaryRoles?.dispatcher || user.secondaryRoles?.answeringService;
  
  // Log for debugging
  // console.log('canReactivateJob check:', { // Removed log
  //   user, // Removed log
  //   hasPrimaryRole, // Removed log
  //   hasSecondaryRole, // Removed log
  //   result: hasPrimaryRole || hasSecondaryRole // Removed log
  // }); // Removed log
  
  return hasPrimaryRole || hasSecondaryRole;
};

// Helper function to determine status priority
export const getStatusPriority = (status) => {
  const priorityMap = {
    'Pending': 1,
    'Scheduled': 2,
    'Pending ETA Approval': 2.5, // Add the new status with priority between Scheduled and Pending Acceptance
    'Pending Acceptance': 3,
    'Pending Driver Acceptance': 3.5,
    'Dispatched': 4,
    'En Route': 5,
    'On Site': 6,
    'Completed': 7,
    'GOA': 8, // GOA and Expired are outside the normal flow
    'Unsuccessful': 9, // Unsuccessful is a terminal state like GOA
    'Expired': 10
  };
  return priorityMap[status] || 0; // Default to 0 for unknown statuses
};

// Helper function to determine job status color
export const getJobStatusColor = (status, job) => {
  if (!status) return 'transparent';
  
  // Check if job has a pending unsuccessful request
  if (job && job.approvalStatusUnsuccessful === 'pending' && job.unsuccessfulReason) {
    return 'rgba(255, 0, 0, 0.2)'; // Red with transparency for unsuccessful requests
  }
  
  // Check if job is expired (Pending Acceptance + past autoRejectAt time)
  if (job && status === 'Pending Acceptance' && job.autoRejectAt) {
    const autoRejectTime = new Date(job.autoRejectAt);
    const now = new Date();
    
    if (now > autoRejectTime) {
      // console.log(`Job ${job.id} is expired (autoRejectAt: ${job.autoRejectAt})`); // Removed log
      return 'rgba(255, 192, 203, 0.3)'; // Pink with transparency for expired jobs
    }
  }
  
  switch (status) {
    // Orange for Dispatched and Awaiting Approval
    case 'Dispatched':
    case 'Awaiting Approval':
      return 'rgba(255, 165, 0, 0.2)'; // Orange with transparency
    
    // Purple for Pending ETA Approval
    case 'Pending ETA Approval':
      return 'rgba(128, 0, 128, 0.15)'; // Purple with transparency
    
    // Light green for En Route
    case 'En Route':
      return 'rgba(144, 238, 144, 0.3)'; // Light green with transparency
    
    // Blue for On Site/On Scene
    case 'On Site':
    case 'On Scene':
      return 'rgba(0, 0, 255, 0.15)'; // Blue with transparency
    
    // Forest green for Completed
    case 'Completed':
      return 'rgba(34, 139, 34, 0.2)'; // Forest green with transparency
    
    // Yellow for GOA
    case 'GOA':
      return 'rgba(255, 255, 0, 0.2)'; // Yellow with transparency
    
    // Red for Unsuccessful
    case 'Unsuccessful':
      return 'rgba(255, 0, 0, 0.2)'; // Red with transparency
    
    // Pink for Expired
    case 'Expired':
      return 'rgba(255, 192, 203, 0.3)'; // Pink with transparency
    
    // Sky blue for Waiting
    case 'Waiting':
      return 'rgba(135, 206, 235, 0.3)'; // Sky blue with transparency
    
    case 'Pending Driver Acceptance':
      return 'rgba(173, 216, 230, 0.3)'; // Light blue with transparency

    // Handle other statuses
    case 'Pending':
    case 'Scheduled':
    case 'Pending Acceptance':
    case 'In-Progress':
    case 'Rejected':
    case 'Accepted':
    case 'Canceled':
      return 'transparent'; // No specific color for these statuses
    
    default:
      // console.log('Unknown status:', status); // Removed log
      return 'transparent';
  }
};

// Helper function to determine if current user is the job provider (dispatcher)
export const isJobProvider = (job, currentUser) => {
  if (!currentUser || !job) return false;
  
  // Convert both IDs to strings for comparison to avoid type issues
  const providerId = job.provider?.toString();
  // Use _id primarily, fall back to id if necessary
  const userId = currentUser._id?.toString() || currentUser.id?.toString(); 
  
  // console.log('Provider check:', { providerId, userId, jobProvider: job.provider, currentUserId: currentUser._id, currentUserIdFallback: currentUser.id, isMatch: providerId === userId }); // Removed log
  return providerId === userId;
};

// Helper to store assigned job in local storage
export const trackAssignedJob = (jobId, userId) => {
  if (!jobId || !userId) return;
  
  try {
    // Get current assigned jobs
    let assignedJobs = {};
    const storedJobs = localStorage.getItem('wk_assigned_jobs');
    
    if (storedJobs) {
      assignedJobs = JSON.parse(storedJobs);
    }
    
    // Add or update this job assignment
    assignedJobs[jobId] = {
      userId,
      timestamp: Date.now()
    };
    
    // Store back to localStorage
    localStorage.setItem('wk_assigned_jobs', JSON.stringify(assignedJobs));
    
    // console.log(`Job ${jobId} tracked as assigned to user ${userId} in localStorage`); // Removed log
  } catch (e) {
    // console.error('Error storing job assignment in localStorage:', e); // Removed log
  }
};

// Helper to check if a job is assigned to a user in local storage
const isJobAssignedToUser = (jobId, userId) => {
  if (!jobId || !userId) return false;
  
  try {
    // Get stored assignments
    const storedJobs = localStorage.getItem('wk_assigned_jobs');
    if (!storedJobs) return false;
    
    const assignedJobs = JSON.parse(storedJobs);
    const assignment = assignedJobs[jobId];
    
    // Check if this job is assigned to this user
    if (assignment && assignment.userId === userId) {
      // console.log(`Found job ${jobId} assigned to user ${userId} in localStorage`); // Removed log
      return true;
    }
  } catch (e) {
    // console.error('Error checking job assignment in localStorage:', e); // Removed log
  }
  
  return false;
};

// Helper to check if a job is assigned to a user in local storage
const isJobAssignedToAnyUser = (jobId) => {
  if (!jobId) return false;
  
  try {
    // Get stored assignments
    const storedJobs = localStorage.getItem('wk_assigned_jobs');
    if (!storedJobs) return false;
    
    const assignedJobs = JSON.parse(storedJobs);
    // Check if this job has any assignment
    return !!assignedJobs[jobId];
  } catch (e) {
    // console.error('Error checking job assignment in localStorage:', e); // Removed log
  }
  
  return false;
};

// Helper to get the user ID a job is assigned to in localStorage
const getAssignedUserIdFromStorage = (jobId) => {
  if (!jobId) return null;
  
  try {
    // Get stored assignments
    const storedJobs = localStorage.getItem('wk_assigned_jobs');
    if (!storedJobs) return null;
    
    const assignedJobs = JSON.parse(storedJobs);
    const assignment = assignedJobs[jobId];
    
    // Return the user ID if found
    if (assignment && assignment.userId) {
      return assignment.userId;
    }
  } catch (e) {
    // console.error('Error getting assigned user ID from localStorage:', e); // Removed log
  }
  
  return null;
};

// Helper function to determine if current user is the assigned driver
export const isAssignedDriver = (job, currentUser) => {
  // --- Enhanced Logging ---
  const logPrefix = `[isAssignedDriver Job: ${job?.id}, User: ${currentUser?.username || currentUser?._id}]`;
  // console.log(`${logPrefix} Function called.`);
  // console.log(`${logPrefix} Job Data:`, { id: job?.id, driverId: job?.driverId, driver: job?.driver, status: job?.status });
  // console.log(`${logPrefix} User Data:`, { id: currentUser?.id, _id: currentUser?._id, name: currentUser?.name, username: currentUser?.username, email: currentUser?.email, primaryRole: currentUser?.primaryRole, secondaryRoles: currentUser?.secondaryRoles });
  // --- End Enhanced Logging ---

  // Early check: if no job, we can't do anything
  if (!job) {
    // console.log(`${logPrefix} Missing job data. Returning false.`);
    return false;
  }
  // During the initial render, currentUser might be null or missing IDs
  // We need special handling for this case, especially for "Pending Acceptance" jobs
  const isUserDataIncomplete = !currentUser || (!currentUser.id && !currentUser._id);

  if (isUserDataIncomplete) {
    // console.log(`${logPrefix} User data incomplete during initial render.`);
    // Even with incomplete user data, we can check if this specific job is in pending acceptance state
    // This ensures drivers see accept/reject buttons as soon as possible during initial page load
    if (job.status === 'Pending Acceptance') {
      // console.log(`${logPrefix} Status is Pending Acceptance with incomplete user data.`);
      // Check if the job has recent assignment information in localStorage
      const isJobInLocalStorage = isJobAssignedToAnyUser(job.id);
      // console.log(`${logPrefix} Check localStorage assignment: ${isJobInLocalStorage}`);

      if (isJobInLocalStorage) {
        const storedUserId = getAssignedUserIdFromStorage(job.id);
        // console.log(`${logPrefix} Job found in localStorage, assigned to user ID: ${storedUserId}. Returning true (provisional).`);
        // Since we don't know the current user yet, we can't determine if this is the correct user
        // We'll let JobsTable show the buttons during initial load if the job is in localStorage
        // The correct visibility will be determined on the next render when user data is available
        return true; // Provisional true
      }

      // If no localStorage assignment, we need to check if this job has a driverId or driver name
      if (job.driverId || job.driver) {
        // console.log(`${logPrefix} Job has driverId (${job.driverId}) or driver name (${job.driver}) but user data incomplete. Returning true (provisional).`);
        // It has driver info, but we can't verify yet if it's the current user
        // Show buttons during initial render, next render will have full user data to verify
        return true; // Provisional true
      }

      // Use user data from localStorage as a fallback if available
      try {
        const storedUserData = localStorage.getItem('user');
        if (storedUserData) {
          const userData = JSON.parse(storedUserData);
          // console.log(`${logPrefix} Found user data in localStorage:`, userData);

          // Check if this is a driver or SP user from localStorage
          const isPossibleAssignee =
            userData.primaryRole === 'SP' ||
            (userData.secondaryRoles &&
              (Array.isArray(userData.secondaryRoles)
                ? userData.secondaryRoles.includes('driver')
                : userData.secondaryRoles.driver));
          // console.log(`${logPrefix} Is user from localStorage a possible assignee? ${isPossibleAssignee}`);

          if (isPossibleAssignee) {
            // console.log(`${logPrefix} User from localStorage (${userData.username}) is possible assignee. Returning true (provisional).`);
            return true; // Provisional true
          }
        } else {
          // console.log(`${logPrefix} No user data found in localStorage.`);
        }
      } catch (e) {
        // console.error(`${logPrefix} Error checking user data in localStorage:`, e);
      }
    }

    // For all other cases with incomplete user data, we can't determine if this user is the assigned driver
    // console.log(`${logPrefix} User data incomplete, not Pending Acceptance or no provisional match found. Returning false.`);
    return false;
  }

  // If we have complete user data, proceed with normal checks
  // console.log(`${logPrefix} User data is complete. Proceeding with checks.`);

  // --- PRIORITIZE DIRECT ID CHECK ---
  // console.log(`${logPrefix} --- Starting Direct ID Check ---`);
  // Get all possible user IDs from the current user
  const userIdFromId = currentUser.id?.toString().trim() || '';
  const userIdFrom_Id = currentUser._id?.toString().trim() || '';
  const userIds = [userIdFromId, userIdFrom_Id].filter(id => id.length > 0);
  // console.log(`${logPrefix} Current User IDs:`, userIds);

  // Enhanced logging for job.driverId
  // console.log(`${logPrefix} Job Driver ID Type:`, typeof job.driverId);
  // console.log(`${logPrefix} Job Driver ID Value:`, job.driverId);
  // if (job.driverId && typeof job.driverId === 'object') {
    // console.log(`${logPrefix} Job Driver ID Properties:`, Object.keys(job.driverId));
    // console.log(`${logPrefix} Job Driver ID._id:`, job.driverId._id);
  // }

  // Check if job.driverId directly matches any of the current user's IDs
  if (job.driverId) {
    // Handle both string and object driverId
    let driverId;
    if (typeof job.driverId === 'object' && job.driverId._id) {
      driverId = job.driverId._id.toString().trim();
    } else {
      driverId = job.driverId.toString().trim();
    }
    
    const matchingId = userIds.find(id => id === driverId);
    const isDirectMatch = !!matchingId;

    // console.log(`${logPrefix} Processed Driver ID for comparison: ${driverId}`);
    // console.log(`${logPrefix} Direct ID Match Result: ${isDirectMatch}`);

    if (isDirectMatch) {
      // console.log(`${logPrefix} Direct ID match found (${matchingId}). Storing in localStorage and returning true.`);
      // If direct match found, store in localStorage and return true immediately
      trackAssignedJob(job.id, matchingId);
      return true; // Definite true
    } else {
      // console.log(`${logPrefix} No direct ID match.`);
    }
  } else {
    // console.log(`${logPrefix} Job does not have a driverId.`);
  }
  // console.log(`${logPrefix} --- Finished Direct ID Check ---`);
  // --- END DIRECT ID CHECK ---

  // --- Fallback Checks (localStorage, name, email) ---
  // console.log(`${logPrefix} --- Starting Fallback Checks ---`);

  // Check localStorage for persistent assignments ONLY if direct ID check failed
  // AND the job doesn't have a driverId (meaning we rely more on localStorage)
  // OR if the localStorage entry matches the current user (redundant check, but safe)
  if (!job.driverId) { // Only check localStorage for current user if job.driverId is missing
      // console.log(`${logPrefix} Job has no driverId. Checking localStorage assignment for current user...`);
      const hasPersistedAssignment = userIds.some(userId =>
          isJobAssignedToUser(job.id, userId)
      );
      // console.log(`${logPrefix} Is job assigned to current user in localStorage? ${hasPersistedAssignment}`);

      if (hasPersistedAssignment) {
          // console.log(`${logPrefix} Found assignment in localStorage (no job.driverId). Returning true.`);
          return true; // Definite true if no driverId and localStorage matches
      }
  } else {
      // console.log(`${logPrefix} Job has driverId (${job.driverId}). Skipping localStorage check for current user assignment.`);
  }


  // Check if the job is assigned to *someone else* in localStorage, regardless of job.driverId
  // This prevents showing buttons if localStorage has stale data for another user, even if name/email checks might pass later.
  // console.log(`${logPrefix} Checking if job is assigned to ANY user in localStorage...`);
  const isJobInLocalStorage = isJobAssignedToAnyUser(job.id);
  // console.log(`${logPrefix} Is job assigned to ANY user in localStorage? ${isJobInLocalStorage}`);
  if (isJobInLocalStorage) { // Check regardless of status now
      const storedUserId = getAssignedUserIdFromStorage(job.id);
      // console.log(`${logPrefix} Job assigned to user ${storedUserId} in localStorage.`);
      // Check if the stored ID is different from the current user's IDs
      const isAssignedToDifferentUser = storedUserId && !userIds.includes(storedUserId); // Ensure storedUserId is not null
      // console.log(`${logPrefix} Is stored user ID different from current user IDs? ${isAssignedToDifferentUser}`);
      if (isAssignedToDifferentUser) {
          // console.log(`${logPrefix} Job assigned to a DIFFERENT user (${storedUserId}) in localStorage. Returning false.`);
          return false; // Definite false - prevents showing buttons due to stale data for others
      }
  }


  // Fallback to name/email checks if direct ID check failed AND localStorage didn't yield a definitive result
  // console.log(`${logPrefix} Checking Name/Email...`);
  const userName = (currentUser.name || currentUser.username || '').toLowerCase().trim();
  // console.log(`${logPrefix} Current User Name/Username (lower, trim): ${userName}`);

  // Check by job.driver (string) and user.name/username
  if (job.driver && typeof job.driver === 'string') {
    const driverName = job.driver.toLowerCase().trim();
    const isNameMatch = userName.length > 0 && driverName.includes(userName);

    // console.log(`${logPrefix} Job Driver Name (lower, trim): ${driverName}`);
    // console.log(`${logPrefix} Name Match Result: ${isNameMatch}`);

    if (isNameMatch) {
      // console.log(`${logPrefix} Name match found. Storing in localStorage and returning true.`);
      // Store by user's primary ID if name matches
      if (userIdFromId) trackAssignedJob(job.id, userIdFromId);
      return true; // Definite true
    } else {
      // console.log(`${logPrefix} No name match.`);
    }
  } else {
    // console.log(`${logPrefix} Job does not have a string driver name.`);
  }

  // Additional check for username in email
  if (job.driver && currentUser.email) {
    const driverName = job.driver.toLowerCase().trim(); // Already lower/trimmed
    const emailName = currentUser.email.split('@')[0].toLowerCase().trim();
    const isEmailMatch = emailName.length > 0 && driverName.includes(emailName);

    // console.log(`${logPrefix} Current User Email Username Part (lower, trim): ${emailName}`);
    // console.log(`${logPrefix} Email Match Result: ${isEmailMatch}`);

    if (isEmailMatch) {
      // console.log(`${logPrefix} Email match found. Storing in localStorage and returning true.`);
      // Store by user's primary ID if email matches
      if (userIdFromId) trackAssignedJob(job.id, userIdFromId);
      return true; // Definite true
    } else {
      // console.log(`${logPrefix} No email match.`);
    }
  } else {
    // console.log(`${logPrefix} Job driver name or user email missing for email check.`);
  }
  // --- End Fallback Checks ---

  // --- Special Handling for Pending Acceptance (Final Fallback) ---
  if (job.status === 'Pending Acceptance') {
    // console.log(`${logPrefix} --- Starting Special Handling for Pending Acceptance ---`);
    
    // Enhanced user role logging
    // console.log(`${logPrefix} User Details:`, {
      // id: currentUser?._id || currentUser?.id,
      // username: currentUser?.username,
      // primaryRole: currentUser?.primaryRole,
      // secondaryRoles: currentUser?.secondaryRoles,
      // vendorNumber: currentUser?.vendorNumber,
      // vendorId: currentUser?.vendorId
    // });
    
    // Log job provider details
    // console.log(`${logPrefix} Job Provider Details:`, {
      // provider: job.provider,
      // driverId: job.driverId,
      // driver: job.driver,
      // needsAcceptance: job.needsAcceptance
    // });
    
    // Check if user has driver role
    let hasDriverRole = false;
    if (currentUser.secondaryRoles) {
      if (Array.isArray(currentUser.secondaryRoles)) {
        hasDriverRole = currentUser.secondaryRoles.includes('driver');
      } else if (typeof currentUser.secondaryRoles === 'object') {
        hasDriverRole = currentUser.secondaryRoles.driver === true;
      }
    }
    // console.log(`${logPrefix} Has Driver Role? ${hasDriverRole}`);

    // Is this user a service provider (SP) or has the SP role?
    const isServiceProvider = currentUser.primaryRole === 'SP';
    // console.log(`${logPrefix} Is Service Provider? ${isServiceProvider}`);

    // Re-check name match here for clarity in this block
    const driverName = (job.driver && typeof job.driver === 'string') ? job.driver.toLowerCase().trim() : '';
    const nameMatch = userName && userName.length > 0 && driverName.includes(userName);
    // console.log(`${logPrefix} Name Match (re-check): ${nameMatch}`);

    // Re-check ID match here for clarity - handle object ID
    let driverIdForMatch;
    if (job.driverId) {
      if (typeof job.driverId === 'object' && job.driverId._id) {
        driverIdForMatch = job.driverId._id.toString().trim();
      } else {
        driverIdForMatch = job.driverId.toString().trim();
      }
    }
    
    const driverIdMatch = driverIdForMatch && userIds.includes(driverIdForMatch);
    // console.log(`${logPrefix} Driver ID for match: ${driverIdForMatch}`);
    // console.log(`${logPrefix} Driver ID Match (re-check): ${driverIdMatch}`);

    // Logic: Return true if (ID matches) OR (user has driver role AND name matches) OR (user is SP AND name matches)
    const finalCheckResult = driverIdMatch || (hasDriverRole && nameMatch) || (isServiceProvider && nameMatch);
    // console.log(`${logPrefix} Final Pending Acceptance Check Result: ${finalCheckResult}`);

    if (finalCheckResult) {
      // console.log(`${logPrefix} Special handling condition met. Storing in localStorage and returning true.`);
      if (userIdFromId) trackAssignedJob(job.id, userIdFromId);
      return true; // Definite true
    } else {
      // console.log(`${logPrefix} Special handling condition not met.`);
    }
    // console.log(`${logPrefix} --- Finished Special Handling ---`);
  } else {
    // console.log(`${logPrefix} Job status is not Pending Acceptance, skipping special handling.`);
  }

  // If all checks fail, the current user is not the assigned driver
  // console.log(`${logPrefix} All checks failed. Returning false.`);
  return false; // Definite false
};

// Get the timestamp to use as the base for ETA calculation
export const getOriginalETATimestamp = (job) => {
  // For jobs where an ETA has been accepted, use the acceptedAt timestamp
  // This ensures the ETA countdown is based on when Org 1 accepted the SP's ETA
  if (job.acceptedAt) {
    return new Date(job.acceptedAt).getTime();
  }

  // If no acceptedAt, fall back to createdAt
  if (job.createdAt) {
    return new Date(job.createdAt).getTime();
  }

  // Final fallback to current time if no timestamps are available
  return Date.now();
};

// Calculate target time for ETA countdown
export const calculateTargetTime = (job) => {
  if (!job.eta) return null;
  
  // Create a unique job identifier for logging
  // eslint-disable-next-line no-unused-vars
  const jobIdentifier = `job-${job.id.substring(0, 6)}`;
  
  // If ETA is a number (minutes), calculate target time from original timestamp
  if (!isNaN(parseInt(job.eta))) {
    // Convert ETA minutes to milliseconds
    const etaMilliseconds = parseInt(job.eta) * 60 * 1000;
    
    // Get the original timestamp to use as the base for ETA calculation
    // This ensures the ETA countdown doesn't reset on redispatch
    const baseTime = getOriginalETATimestamp(job);
    
    // Calculate the target time by adding the ETA to the base time
    return baseTime + etaMilliseconds;
  }
  
  // If ETA is a scheduled date/time string, convert it to timestamp
  if (typeof job.eta === 'string' && job.eta.includes('Scheduled for')) {
    const dateTimeStr = job.eta.replace('Scheduled for ', '');
    return new Date(dateTimeStr).getTime();
  }
  
  return null;
};

// Calculate auto-rejection time (2 minutes from assignment)
export const calculateAutoRejectTime = (job) => {
  if (!job.autoRejectAt) return null;
  return new Date(job.autoRejectAt).getTime();
};

// Helper function to identify if a user is an Org 2 driver-only user
export const isOrg2DriverOnly = (user) => {
  if (!user) return false;
  
  // Must have organizationLevel 2
  const isOrg2 = user.organizationLevel === 2;
  
  // Primary role must be 'N/A'
  const hasNAPrimaryRole = user.primaryRole === 'N/A';
  
  // Must have only 'driver' as secondary role
  const secondaryRoles = user.secondaryRoles || {};
  const activeSecondaryRoles = Object.keys(secondaryRoles)
    .filter(role => secondaryRoles[role] === true);
  const hasOnlyDriverSecondaryRole = activeSecondaryRoles.length === 1 && 
                                    secondaryRoles.driver === true;
  
  return isOrg2 && hasNAPrimaryRole && hasOnlyDriverSecondaryRole;
};

// Helper function for filtering jobs based on status and user role
export const filterJobs = (jobs, jobCategory, currentUser) => {
  if (jobCategory === 'pending') {
    // Check if user is an Org 2 driver-only user
    const isDriverOnly = isOrg2DriverOnly(currentUser);
    
    return jobs.filter((job) => {
      // For Org 2 driver-only users, only show jobs in Pending Acceptance status that are assigned to them
      if (isDriverOnly) {
        return job.status === 'Pending Acceptance' && isAssignedDriver(job, currentUser);
      }

      // For all other users, use the standard filtering
      const isPending = job.status === 'Pending';
      const isPendingAcceptance = job.status === 'Pending Acceptance';
      const isPendingDriverAcceptance = job.status === 'Pending Driver Acceptance';
      const isWaiting = job.status === 'Waiting';
      const isPendingETAApproval = job.status === 'Pending ETA Approval';
      const isSearchingSP = job.status === 'Searching SP';
      const isBlastDispatch = job.status === 'Blast Dispatch';

      // For drivers, exclude expired 'Pending Acceptance' jobs
      if (currentUser?.primaryRole === 'driver' && isPendingAcceptance) {
        // Check if job is expired (Pending Acceptance + past autoRejectAt time)
        const isExpired = job.autoRejectAt && new Date() > new Date(job.autoRejectAt);
        if (isExpired) {
          return false; // Exclude this job
        }
      }

      // Standard filtering for non-Org2-driver-only users
      return isPending || isPendingAcceptance || isWaiting || isSearchingSP || isPendingETAApproval || isPendingDriverAcceptance || isBlastDispatch;
    });
  } else if (jobCategory === 'inProgress') {
    // For inProgress, exclude jobs that should be in the Scheduled tab
    return isDispatchUser(currentUser)
      ? jobs.filter((job) => {
          // Check if this is a scheduled job that should be in the Scheduled tab
          const isScheduledJob = job.eta && job.eta.includes('Scheduled for') && 
                                ['Scheduled', 'Waiting', 'Dispatched'].includes(job.status);
          
          // If it's a scheduled job that belongs in the Scheduled tab, exclude it
          if (isScheduledJob) return false;
          
          // Otherwise, include jobs with these statuses
          return ['In-Progress', 'Dispatched', 'En Route', 'On Site', 'Awaiting Approval', 'Rejected', 'Accepted', 'Completed', 'Pending', 'Pending Acceptance'].includes(job.status);
        })
      : jobs.filter((job) => {
          // Check if this is a scheduled job that should be in the Scheduled tab
          const isScheduledJob = job.eta && job.eta.includes('Scheduled for') && 
                                ['Scheduled', 'Waiting', 'Dispatched'].includes(job.status);
          
          // If it's a scheduled job that belongs in the Scheduled tab, exclude it
          if (isScheduledJob) return false;
          
          // Otherwise, include jobs with these statuses
          return ['In-Progress', 'Dispatched', 'En Route', 'On Site', 'Awaiting Approval', 'Rejected', 'Accepted'].includes(job.status);
        });
  } else if (jobCategory === 'scheduled') {
    // For scheduled tab, only include jobs with scheduled ETA and appropriate status
    return jobs.filter((job) => 
      // Check if the job has a scheduled ETA
      job.eta && job.eta.includes('Scheduled for') && 
      // And has one of these statuses
      ['Scheduled', 'Waiting', 'Dispatched'].includes(job.status)
    );
  } else if (jobCategory === 'completed') {
    return jobs.filter((job) => job.status === 'Completed' || job.status === 'GOA' || job.status === 'Unsuccessful');
  } else if (jobCategory === 'canceled') {
    return jobs.filter((job) => job.status === 'Canceled');
  }
  
  return [];
};

// Helper function to check if a user can manage unsuccessful requests
export const canManageUnsuccessful = (user) => {
  if (!user) return false;
  
  // Check if the user has the required role (OW, sOW, RM)
  const allowedPrimaryRoles = ['OW', 'sOW', 'OWp', 'RM'];
  const allowedSecondaryRoles = ['dispatcher', 'answeringService', 'admin'];
  
  const hasAllowedPrimaryRole = allowedPrimaryRoles.includes(user.primaryRole);
  
  // Check if user has any of the allowed secondary roles
  let hasAllowedSecondaryRole = false;
  if (user.secondaryRoles) {
    if (Array.isArray(user.secondaryRoles)) {
      // If secondaryRoles is an array, check if it includes any of the allowed roles
      hasAllowedSecondaryRole = allowedSecondaryRoles.some(role => user.secondaryRoles.includes(role));
    } else if (typeof user.secondaryRoles === 'object') {
      // If secondaryRoles is an object, check if any of the allowed roles is true
      hasAllowedSecondaryRole = allowedSecondaryRoles.some(role => user.secondaryRoles[role]);
    }
  }
  
  // Log for debugging
  // console.log('canManageUnsuccessful check:', { // Removed log
  //   user, // Removed log
  //   hasAllowedPrimaryRole, // Removed log
  //   hasAllowedSecondaryRole, // Removed log
  //   primaryRole: user.primaryRole, // Removed log
  //   secondaryRoles: user.secondaryRoles // Removed log
  // }); // Removed log
  
  // User must have either an allowed primary role OR an allowed secondary role
  return hasAllowedPrimaryRole || hasAllowedSecondaryRole;
};

/**
 * Check if a user can approve or deny GOA requests.
 * 
 * A user can approve/deny GOA if:
 * 1. Their primary role is NOT 'N/A'
 * 2. They do NOT have 'driver' as their only secondary role
 * 3. They have access to the job (job is visible to them)
 * 
 * @param {Object} user - The user object
 * @param {Object} job - The job object
 * @returns {boolean} - Whether the user can approve/deny GOA
 */
export const canApproveGOA = (user, job) => {
  if (!user || !job) return false;
  
  // Check if user's primary role is not N/A
  const hasValidPrimaryRole = user.primaryRole !== 'N/A';
  
  // Check if user is not a driver-only user
  let isDriverOnly = false;
  
  if (user.secondaryRoles) {
    if (Array.isArray(user.secondaryRoles)) {
      // If secondaryRoles is an array, check if it only contains 'driver'
      isDriverOnly = user.secondaryRoles.length === 1 && user.secondaryRoles.includes('driver');
    } else if (typeof user.secondaryRoles === 'object') {
      // If secondaryRoles is an object, check if only driver is true
      const roles = Object.keys(user.secondaryRoles).filter(role => user.secondaryRoles[role]);
      isDriverOnly = roles.length === 1 && roles[0] === 'driver';
    }
  }
  
  // Check if job is visible to the user
  let hasJobAccess = false;
  
  if (job.visibleTo) {
    if (Array.isArray(job.visibleTo)) {
      // Convert user ID to string for comparison
      const userId = user._id?.toString() || user.id?.toString();
      hasJobAccess = job.visibleTo.some(id => id.toString() === userId);
    }
  }
  
  // Log for debugging
  // console.log('canApproveGOA check:', { // Removed log
  //   user, // Removed log
  //   job: job.id, // Removed log
  //   hasValidPrimaryRole, // Removed log
  //   isDriverOnly, // Removed log
  //   hasJobAccess, // Removed log
  //   result: hasValidPrimaryRole && !isDriverOnly && hasJobAccess // Removed log
  // }); // Removed log
  
  // User must meet all three conditions
  return hasValidPrimaryRole && !isDriverOnly && hasJobAccess;
};

// Helper function to check if a user is a "driver only" user
export const isDriverOnlyUser = (user) => {
  if (!user) return false;
  
  // Check if primary role is "N/A"
  const hasNAPrimaryRole = user.primaryRole === 'N/A';
  
  // Check if the only secondary role is "driver"
  let hasOnlyDriverSecondaryRole = false;
  
  if (user.secondaryRoles) {
    if (Array.isArray(user.secondaryRoles)) {
      // If secondaryRoles is an array, check if it only contains "driver"
      hasOnlyDriverSecondaryRole = user.secondaryRoles.length === 1 && user.secondaryRoles.includes('driver');
    } else if (typeof user.secondaryRoles === 'object') {
      // If secondaryRoles is an object, check if only driver is true
      const roles = Object.keys(user.secondaryRoles).filter(role => user.secondaryRoles[role]);
      hasOnlyDriverSecondaryRole = roles.length === 1 && roles[0] === 'driver';
    }
  }
  
  return hasNAPrimaryRole && hasOnlyDriverSecondaryRole;
};

/**
 * Determines if the user can approve or deny an ETA submitted by an SP.
 * 
 * This function checks if:
 * 1. The user belongs to Org 1 (OW, sOW, RM assigned to the job's region, or N/A with admin/dispatcher/answeringService roles)
 * 2. For Org 1 users, secondary roles must include admin, dispatcher, or answeringService, OR primary role must be OW, sOW, RM
 *
 * @param {Object} user - The current user object
 * @param {Object} job - The job with 'Pending ETA Approval' status
 * @returns {boolean} - Whether the user can approve/deny the ETA
 */
export const canApproveDenyETA = (user, job) => {
  if (!user || !job) return false;
  
  // Check if Org 1 user
  const isOrg1 = user.organizationLevel === 1;
  
  if (!isOrg1) return false;
  
  // Owner or Sub-Owner can always approve/deny
  if (user.primaryRole === 'OW' || user.primaryRole === 'sOW') {
    return true;
  }
  
  // Check if the user has the required secondary roles
  const hasManagementRole = user.secondaryRoles?.admin === true ||
                            user.secondaryRoles?.dispatcher === true ||
                            user.secondaryRoles?.answeringService === true;
  
  if (hasManagementRole) {
    return true;
  }
  
  // If RM, they need to be assigned to the job's region (but we assume this is already filtered at the backend)
  if (user.primaryRole === 'RM' && user.regions && user.regions.length > 0) {
    return true;
  }
  
  // Default - cannot approve/deny
  return false;
};

/**
 * Determines if the user can approve or deny an ETA submitted by an Org 1 driver.
 *
 * This function checks if:
 * 1. The user belongs to Org 1
 * 2. The user has one of these primary roles: OW, sOW, RM
 * 3. OR the user has any of these secondary roles: admin, dispatcher
 *
 * @param {Object} user - The current user object
 * @param {Object} job - The job with 'Pending ETA Approval' status containing pendingOrg1DriverETA
 * @returns {boolean} - Whether the user can approve/deny the Org 1 driver ETA
 */
export const canApproveOrg1DriverETA = (user, job) => {
  if (!user || !job) return false;
  
  // Check if job is in the correct state
  const isEtaPending = job.status === 'Pending ETA Approval' && job.pendingOrg1DriverETA;
  
  if (!isEtaPending) return false;
  
  // Check if Org 1 user
  const isOrg1 = user.organizationLevel === 1;
  
  if (!isOrg1) return false;
  
  // Check if user has appropriate primary role (OW, sOW, RM)
  const hasAppropriatePrimaryRole =
    user.primaryRole === 'OW' ||
    user.primaryRole === 'sOW' ||
    user.primaryRole === 'RM';
  
  // Check if user has appropriate secondary roles (admin, dispatcher)
  const secondaryRoles = user.secondaryRoles || {};
  const hasAppropriateSecondaryRole =
    (typeof secondaryRoles === 'object' &&
      (secondaryRoles.admin === true || secondaryRoles.dispatcher === true));
  
  // User must have either an appropriate primary role OR an appropriate secondary role
  return hasAppropriatePrimaryRole || hasAppropriateSecondaryRole;
};

// Helper function to get auth headers
export const authHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};
